import React, { useState, useEffect, useRef, useCallback } from "react";
import { View, ScrollView, StyleSheet } from "react-native";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import { DownArrow } from "../../components/svgs/common";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomText from "../../components/common/general/CustomText/CustomText";
import ProcessingMessageBox from "../../components/chat/LoadingMessage/ProcessingMessageBox";
import { useFocusEffect } from "@react-navigation/native";
import { spacing } from "../../styles/design-system";
import type { MessageItem } from "./companionMessage.types";
import type { ReadAloudControl } from "../../components/chatNewInterface/interfaceComponents/messageBubble/ReadAloudPlaybackControl";

import type { DeepResearchProgressProps, DeepResearchResultProps } from "../../components/companion/DeepResearch";
import { MessageRow, getImageGenerationState } from "./MessageRow";

interface MessagesContainerProps {
  messages: MessageItem[];
  isStreaming?: boolean;
  currentStreamingMessageId?: string | null;
  resetAutoScroll?: number;
  onStartEdit?: (messageId: string, messageIndex: number, text: string) => void;
  editingMessageId?: string | null;
  editingMessageDraft?: string | null;
  onChangeEditingMessageDraft?: (value: string) => void;
  onSaveEdit?: () => void;
  onCancelEdit?: () => void;
  onSwitchBranch?: (anchorMessageId: string, targetBranchId: string | null) => void;
  feedbackByMessageId?: Record<string, "up" | "down">;
  onFeedback?: (messageId: string, value: "up" | "down" | null) => void;
  onOpenLearningActivity?: (ileId: string) => void;
  streamStatusMessage?: string | null;
  showIleCreationLoader?: boolean;
  ileCreationErrorMessage?: string | null;
  getAssistantReadAloudControl?: (
    messageId: string,
    content: string,
    meters?: { streamAudioLevel: number; streamAudioLevels: number[] }
  ) => ReadAloudControl | undefined;
  streamAudioLevel?: number;
  streamAudioLevels?: number[];
  deepResearchState?: {
    phase: "progress" | "result" | null;
    progress?: DeepResearchProgressProps;
    result?: DeepResearchResultProps;
  } | null;
  onCancelDeepResearch?: () => void;
}

const CONTAINER_PADDING_BOTTOM = 16;

// Hoisted so the default for `streamAudioLevels` keeps a stable reference across
// renders — a fresh `[]` each render would otherwise bust every MessageRow's memo.
const EMPTY_AUDIO_LEVELS: number[] = [];

const MessagesContainer = ({
  messages,
  isStreaming = false,
  currentStreamingMessageId,
  resetAutoScroll,
  onStartEdit,
  editingMessageId,
  editingMessageDraft,
  onChangeEditingMessageDraft,
  onSaveEdit,
  onCancelEdit,
  onSwitchBranch,
  feedbackByMessageId,
  onFeedback,
  onOpenLearningActivity,
  streamStatusMessage = null,
  showIleCreationLoader = false,
  ileCreationErrorMessage = null,
  getAssistantReadAloudControl,
  streamAudioLevel = 0,
  streamAudioLevels = EMPTY_AUDIO_LEVELS,
  deepResearchState,
  onCancelDeepResearch,
}: MessagesContainerProps) => {
  const scrollViewRef = useRef(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [downButton, setDownButton] = useState(false);
  const [humanMessageOffset, setHumanMessageOffset] = useState(0);
  const [scrollViewHeight, setScrollViewHeight] = useState(undefined);
  const [parentHeight, setParentHeight] = useState(0);

  const [isUserInteracted, setIsUserInteracted] = useState(false);

  const isAtBottomRef = useRef(true);

  useFocusEffect(
    useCallback(() => {
      return () => {
        setIsUserInteracted(false);
      };
    }, [])
  );

  const handleScroll = useCallback((event) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    const isAtBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 50;

    if (!isAtBottom) {
      setIsUserScrolling(true);
      setDownButton(true);
      setIsUserInteracted(true); // User scrolled up, stop auto-scrolling
    } else {
      setIsUserScrolling(false);
      setDownButton(false);
    }

    isAtBottomRef.current = isAtBottom;
  }, []);

  const scrollToBottom = (animated = false) => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollToEnd({ animated });
    }
  };

  const scrollToBottomButton = () => {
    setDownButton(false);
    scrollToBottom(true);
  };

  useEffect(() => {
    if (scrollViewRef.current) {
      scrollViewRef.current.setAttribute("data-sensitive", "true");
    }
  }, []);

  // Reset user interaction and scroll height when resetAutoScroll changes (e.g., when new message is submitted or chat switches)
  useEffect(() => {
    if (resetAutoScroll !== undefined) {
      setIsUserInteracted(false);
      setScrollViewHeight(undefined);
      setHumanMessageOffset(0);
    }
  }, [resetAutoScroll]);

  // Reset scroll height when chat switch
  useEffect(() => {
    if (messages.length === 0) {
      setScrollViewHeight(undefined);
      setHumanMessageOffset(0);
    }
  }, [messages.length]);

  useEffect(() => {
    if (!scrollViewRef.current) return;

    // Only auto-scroll when the user has not manually scrolled up.
    // This prevents branch switches (which change layout/offsets) from forcing a jump to the bottom
    // when the user is reviewing earlier messages.
    if (!isUserInteracted) {
      scrollToBottom();
    }
  }, [humanMessageOffset, isUserInteracted]);

  useEffect(() => {
    if (!messages) return;
    if (messages.length === 0) return;

    if (!isUserInteracted) {
      scrollToBottom();
    }
  }, [messages]);

  const handleLastHumanLayout = useCallback(
    (offsetY: number) => {
      setHumanMessageOffset(offsetY);
      setScrollViewHeight(offsetY + parentHeight - CONTAINER_PADDING_BOTTOM);
    },
    [parentHeight]
  );

  const streamingMessage = currentStreamingMessageId
    ? messages.find((message) => message.id === currentStreamingMessageId)
    : undefined;
  const deepResearchInProgress = deepResearchState?.phase === "progress";
  const deepResearchUiVisible = deepResearchState?.phase === "progress" || deepResearchState?.phase === "result";
  const shouldShowProcessingFallback =
    isStreaming && !deepResearchUiVisible && (!streamingMessage || !streamingMessage.content);
  const lastRawAssistantMessageId = [...(messages ?? [])].reverse().find((item) => item.sender === "ai")?.id;

  // Filter out empty assistant messages that are currently streaming,
  // but keep them if they have an image generation state (loading card must remain visible).
  const visibleMessages = messages?.filter((item) => {
    const imageGeneration = getImageGenerationState(item);
    if (
      isStreaming &&
      item.id === currentStreamingMessageId &&
      item.sender === "ai" &&
      !item.content?.trim() &&
      !imageGeneration
    ) {
      return false;
    }
    if (
      deepResearchInProgress &&
      item.id === lastRawAssistantMessageId &&
      item.sender === "ai" &&
      !item.content?.trim() &&
      !imageGeneration
    ) {
      return false;
    }
    return true;
  });
  const lastHumanMessageId = [...(visibleMessages ?? [])].reverse().find((item) => item.sender === "human")?.id;
  const lastAssistantMessageId = [...(visibleMessages ?? [])].reverse().find((item) => item.sender === "ai")?.id;

  const statusToShow = deepResearchInProgress ? null : streamStatusMessage;
  const lastVisibleMessage = visibleMessages?.[visibleMessages.length - 1];
  const lastVisibleIsAssistant = lastVisibleMessage?.sender === "ai";

  return (
    <View
      style={[styles.container, { paddingBottom: CONTAINER_PADDING_BOTTOM }]}
      onLayout={(event) => setParentHeight(event.nativeEvent.layout.height)}>
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        maintainVisibleContentPosition={{
          minIndexForVisible: 0,
          autoscrollToTopThreshold: 10,
        }}
        showsVerticalScrollIndicator={true}
        contentContainerStyle={{ height: scrollViewHeight }}
        onContentSizeChange={() => {
          if (!isUserInteracted) {
            scrollToBottom();
          }
        }}
        tabIndex={0}>
        <View style={styles.view}>
          {visibleMessages?.map((item, index, array) => {
            const isLastAssistant = index === array.length - 1 && item.sender === "ai";
            const isLastHuman = item.id === lastHumanMessageId;
            const isBeingEdited = editingMessageId === item.id;
            const deepResearchResult =
              item.sender === "ai" &&
              deepResearchState?.phase === "result" &&
              deepResearchState.result &&
              (item.id === currentStreamingMessageId ||
                (currentStreamingMessageId == null && item.id === lastAssistantMessageId))
                ? deepResearchState.result
                : undefined;
            const deepResearchProgress =
              isLastHuman && deepResearchState?.phase === "progress" ? deepResearchState.progress : undefined;

            return (
              <MessageRow
                key={item.id || index}
                item={item}
                messageIndex={index}
                isStreaming={isStreaming}
                currentStreamingMessageId={currentStreamingMessageId}
                isLastAssistant={isLastAssistant}
                isLastHuman={isLastHuman}
                isBeingEdited={isBeingEdited}
                editValue={isBeingEdited ? (editingMessageDraft ?? item.content ?? "") : undefined}
                onStartEdit={onStartEdit}
                onCancelEdit={onCancelEdit}
                onChangeEditingMessageDraft={onChangeEditingMessageDraft}
                onSaveEdit={onSaveEdit}
                onSwitchBranch={onSwitchBranch}
                feedback={item.sender === "ai" ? feedbackByMessageId?.[item.id] : undefined}
                onFeedback={onFeedback}
                getAssistantReadAloudControl={getAssistantReadAloudControl}
                streamAudioLevel={streamAudioLevel}
                streamAudioLevels={streamAudioLevels}
                status={isLastAssistant ? statusToShow : null}
                showIleCreationLoader={isLastAssistant ? showIleCreationLoader : false}
                ileCreationErrorMessage={isLastAssistant ? ileCreationErrorMessage : null}
                onOpenLearningActivity={onOpenLearningActivity}
                deepResearchResult={deepResearchResult}
                deepResearchProgress={deepResearchProgress}
                onCancelDeepResearch={onCancelDeepResearch}
                onLastHumanLayout={handleLastHumanLayout}
              />
            );
          })}
          {shouldShowProcessingFallback && (
            <View>
              <CustomSpacing type="vertical" size="s" />
              <ProcessingMessageBox />
            </View>
          )}
          {statusToShow && !lastVisibleIsAssistant ? (
            <View style={styles.statusMessageRow}>
              <CustomSpacing type="vertical" size="s" />
              <CustomText useTranslationText={false} text={statusToShow} size="s" style={styles.statusMessageText} />
            </View>
          ) : null}
        </View>
      </ScrollView>
      {downButton && (
        <View style={styles.buttonContainer}>
          <CustomButton
            styleType="primary"
            style={styles.button}
            leftIcon={<DownArrow />}
            onPress={scrollToBottomButton}
            rightIcon={undefined}
            text={undefined}
            textProps={undefined}
            textStyle={undefined}
            disabled={false}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    height: 20,
    paddingHorizontal: 10,
    zIndex: 0,
  },
  view: {
    width: "100%",
    alignSelf: "center",
    maxWidth: 768,
    paddingHorizontal: 10,
  },
  button: {
    justifyContent: "center",
    position: "absolute",
    alignItems: "center",
    bottom: 10,
    width: 30,
    height: 30,
    borderRadius: 25,
    zIndex: 5,
  },
  buttonContainer: {
    alignItems: "center",
  },
  statusMessageRow: {
    marginLeft: 40,
    paddingVertical: spacing.xs,
  },
  statusMessageText: {
    fontStyle: "italic",
    opacity: 0.8,
  },
});

export default MessagesContainer;
